'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PhilosophyPrinciple {
  id: number;
  icon: string;
  title: string;
  description: string;
}

export default function PhilosophySection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const principles: PhilosophyPrinciple[] = [
    {
      id: 1,
      icon: 'ChartBarIcon',
      title: 'Analytical + Creative',
      description: 'We combine financial analysis expertise with creative marketing execution. This rare blend means we understand both the ROI metrics and the creative strategies that drive growth.',
    },
    {
      id: 2,
      icon: 'MapPinIcon',
      title: 'Local Triangle Focus',
      description: 'Based in Raleigh, we understand the Triangle area market. Our local knowledge combined with digital expertise helps your business connect with the community and grow.',
    },
    {
      id: 3,
      icon: 'UserIcon',
      title: 'Personalized Service',
      description: 'We\'re not a large agency—we\'re your dedicated digital growth partner. Every client receives personalized attention and strategies tailored to their specific business goals.',
    },
    {
      id: 4,
      icon: 'ArrowTrendingUpIcon',
      title: 'Growth-Focused Results',
      description: 'Nearly six years of hands-on experience in paid marketing, social media, and web development. We focus on practical solutions that deliver measurable business growth.',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full px-6 lg:px-12 py-32 lg:py-48 bg-secondary"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div
          className={`max-w-3xl mb-24 transition-all duration-slow ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-sm font-cta font-semibold tracking-[0.2em] text-muted-foreground uppercase mb-6">
            Why Valinaire
          </p>
          <h2 className="text-4xl lg:text-6xl font-headline font-bold leading-[1.1] text-primary mb-8">
            What Makes Us Different
          </h2>
          <p className="text-xl font-body text-text-secondary leading-relaxed">
            Our unique background and approach set us apart from typical digital agencies. We bring a rare combination of skills and local expertise to every partnership.
          </p>
        </div>

        {/* Principles Grid */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {principles.map((principle, index) => (
            <div
              key={principle.id}
              className={`group transition-all duration-slow ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="space-y-6">
                {/* Icon */}
                <div className="w-16 h-16 flex items-center justify-center bg-primary text-primary-foreground transition-transform duration-fast group-hover:scale-110">
                  <Icon name={principle.icon as any} size={32} variant="outline" />
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-2xl lg:text-3xl font-headline font-bold text-primary">
                    {principle.title}
                  </h3>
                  <p className="text-lg font-body text-text-secondary leading-relaxed">
                    {principle.description}
                  </p>
                </div>

                {/* Decorative Line */}
                <div className="w-0 h-[2px] bg-primary transition-all duration-medium group-hover:w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}